import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { server } from '../server'; 
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import UserBanner from '../components/Shop/UserBanner';
import { useParams } from 'react-router-dom';
import UserProfileData from '../components/UserProfileData';
import MyImages from '../components/Content/MyImages';
import MyContentCarousel from '../components/Profile/MyContentCarousel';
import FollowButton from '../components/Connections/FollowButton';
import FollowersDisplay from '../components/Connections/FollowersDisplay';
import MyPostWidgetRedux from '../widgets/MyPostWidgetRedux';
import MixedSponsoredAds from '../components/Sponsored/MixedSponsoredAds';
import RoomsProfile from '../components/Rooms/RoomsProfile';
import UserAvatar from '../components/Layout/UserAvatar';
import FollowingDisplay from '../components/Connections/FollowingDisplay';
import Loader from '../components/Layout/Loader';
import EventsParentContainer from '../components/Events/EventsParentContainer';
import { useSelector } from 'react-redux';
import ShareToSocialMedia from '../components/Shop/ShareToSocialMedia';
import { buildPublicAppUrl } from '../utils/publicApp';
import '../stylesheets/UserProfileHeroPills.css';
import '../stylesheets/ProfileFeatureWidgetRow.css';

const PublicProfilePage = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 1) Renamed `id` to `handle`:
  const { handle } = useParams();
  const { user: currentUser } = useSelector((state) => state.user);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // 2) Fetch user by handle instead of by id:
        const response = await axios.get(`${server}/user/user-info/handle/${handle}`);
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [handle]);

  if (isLoading) {
    return <Loader />;
  }

  if (!user) {
    return <div>User data not available</div>;
  }

  // 3) Use `user._id` for child components
  const userId = user._id;
  const isOwner = userId === currentUser?._id;
  const profileShareUrl = typeof window !== 'undefined' ? window.location.href : buildPublicAppUrl(`/user/${user.handle}`);

  return (
    <div className="profile-page public-profile-page">
      <Header />
      <Sidebar />
      <div className="profile-banner-wrapper">
        <UserBanner isUser={false} user={user} setUser={setUser} />
        <div className="profile-info-container">
          <div className="profile-hero-layout">
            <UserAvatar user={user} />
            <div className="profile-info">
              <div className="profile-name-wrapper">
                <div className="profile-name-and-button">
                  <h3 className="profile-name">{user.name}</h3>
                </div>
                <h3 className="profile-handle">@{user.handle}</h3>
                <div className="profile-friends">
                  <FollowersDisplay userId={userId} profileType="User" />
                  <FollowingDisplay userId={userId} profileType="User" />
                </div>
                <FollowButton
                  className="profile-followers-add-button profile-public-follow-button"
                  connectionId={userId}
                  connectionType="User"
                />
              </div>
            </div>
          </div>
        </div>
      </div> 
      
      <div className="profile-main-content">
        <div className="profile-sidebar-left">
          <RoomsProfile
            profileId={userId}
            profileType="User"
            canCreate={isOwner}
            showActionMenu={isOwner}
            emptyMessage={isOwner ? 'Create your first community to get started.' : `${user.name} has not created any communities yet.`}
          />
          <MyImages
            userId={userId}
            canAddPhotos={isOwner}
            emptyActionHref={isOwner ? '#profile-post-composer' : undefined}
            emptyMessage={
              isOwner
                ? 'Share a photo post to start your gallery.'
                : 'This profile has not shared any photos yet.'
            }
            ownerName={user.name}
            ownerAvatar={user.avatar?.url}
          />
          <div className="profile-share-sidebar-card">
            <h5>Share Profile</h5>
            <ShareToSocialMedia
              url={profileShareUrl}
              message={`Check out ${user.name} on MATRIX.`}
              profileName={user.name}
              profileHandle={user.handle}
              qrCodeUrl={user.qrCodeUrl || ''}
              title="Share Profile"
            />
          </div>
          <EventsParentContainer ownerId={userId} />
        </div>
        
        <main className="profile-main-area">
          <div id="profile-post-composer" className="profile-post-composer-anchor">
            <MyPostWidgetRedux />
          </div>
          <div className="profile-feature-widget-row profile-mobile-feature-widget-row" aria-label="Profile media widgets">
            <section className="profile-feature-widget profile-feature-widget-photos">
              <MyImages
                userId={userId}
                canAddPhotos={isOwner}
                emptyActionHref={isOwner ? '#profile-post-composer' : undefined}
                emptyMessage={
                  isOwner
                    ? 'Share a photo post to start your gallery.'
                    : 'This profile has not shared any photos yet.'
                }
                ownerName={user.name}
                ownerAvatar={user.avatar?.url}
              />
            </section>
            <section className="profile-feature-widget profile-feature-widget-memes">
              <MyContentCarousel ownerId={userId} profileType="User" canCreate={isOwner} showActionMenu={isOwner} />
            </section>
            <section id="profile-communities-widget" className="profile-feature-widget profile-feature-widget-communities">
              <RoomsProfile
                profileId={userId}
                profileType="User"
                canCreate={isOwner}
                showActionMenu={isOwner}
                emptyMessage={isOwner ? 'Create your first community.' : `${user.name} has not created any communities yet.`}
              />
            </section>
          </div>
          <UserProfileData initialUser={user} />
        </main>
        
        <aside className="profile-sidebar-right">
          <MyContentCarousel ownerId={userId} profileType="User" canCreate={isOwner} />
          <MixedSponsoredAds />
        </aside>
      </div>
    </div>
  );
};

export default PublicProfilePage;


